/**
 * Decoding (W1-14, A§8): `decodeAudioData` on the main thread (the only universally available decoder) for the
 * music, skin sounds and custom hitsounds; channel extraction for the analysis worker; container sniffing so the
 * media-element path gets a typed Blob.
 */

import { AudioEngineError } from './errors';

/** The part of a BaseAudioContext needed for decoding. */
export interface DecodeContext {
  decodeAudioData(
    data: ArrayBuffer,
    success?: (buffer: AudioBuffer) => void,
    error?: (e: DOMException) => void,
  ): Promise<AudioBuffer>;
}

/** A private copy of the bytes: decodeAudioData detaches its argument, and callers keep their own. */
export function copyBytes(bytes: ArrayBuffer | Uint8Array): ArrayBuffer {
  if (bytes instanceof Uint8Array) {
    const out = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(out).set(bytes);
    return out;
  }
  return bytes.slice(0);
}

/**
 * Decodes compressed audio into an AudioBuffer at the context's sample rate. Works with both the promise and the
 * legacy callback form of decodeAudioData. Failures become `AudioEngineError('decodeFailed')`.
 */
export function decodeAudio(ctx: DecodeContext, bytes: ArrayBuffer | Uint8Array): Promise<AudioBuffer> {
  const data = copyBytes(bytes);
  return new Promise<AudioBuffer>((resolve, reject) => {
    let settled = false;
    const ok = (buffer: AudioBuffer): void => {
      if (settled) return;
      settled = true;
      resolve(buffer);
    };
    const fail = (e: unknown): void => {
      if (settled) return;
      settled = true;
      reject(new AudioEngineError('decodeFailed', e instanceof Error ? e.message : undefined, { cause: e }));
    };
    try {
      const result = ctx.decodeAudioData(data, ok, fail) as Promise<AudioBuffer> | undefined;
      if (result !== undefined && typeof result.then === 'function') result.then(ok, fail);
    } catch (e) {
      fail(e);
    }
  });
}

/**
 * Copies of every channel's PCM (fresh Float32Arrays, safe to transfer to a worker without detaching the
 * AudioBuffer's own storage).
 */
export function copyChannels(buffer: AudioBuffer): Float32Array[] {
  const out: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) out.push(new Float32Array(buffer.getChannelData(c)));
  return out;
}

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  let s = '';
  for (let i = 0; i < length && offset + i < bytes.length; i++) s += String.fromCharCode(bytes[offset + i] ?? 0);
  return s;
}

/**
 * MIME type of an audio container from its magic bytes (Ogg, MP3 with or without ID3, WAV, FLAC, MP4/M4A, WebM,
 * AAC ADTS, AIFF); '' when unknown (the media element then sniffs by itself).
 */
export function sniffAudioMime(bytes: Uint8Array): string {
  if (ascii(bytes, 0, 4) === 'OggS') return 'audio/ogg';
  if (ascii(bytes, 0, 4) === 'fLaC') return 'audio/flac';
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WAVE') return 'audio/wav';
  if (ascii(bytes, 0, 4) === 'FORM' && (ascii(bytes, 8, 4) === 'AIFF' || ascii(bytes, 8, 4) === 'AIFC')) {
    return 'audio/aiff';
  }
  if (ascii(bytes, 4, 4) === 'ftyp') return 'audio/mp4';
  if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) return 'audio/webm';
  if (ascii(bytes, 0, 3) === 'ID3') return 'audio/mpeg';
  const b0 = bytes[0] ?? 0;
  const b1 = bytes[1] ?? 0;
  if (b0 === 0xff && (b1 & 0xf6) === 0xf0) return 'audio/aac'; // ADTS (layer bits 00)
  if (b0 === 0xff && (b1 & 0xe0) === 0xe0) return 'audio/mpeg'; // MPEG audio frame sync
  return '';
}
